import { axiosClient } from "@/lib/axios";
import { queryClient } from "@/lib/react-query";
import { HttpError } from "@/types/error";
import { PublicRecording } from "@classview/shared-types";
import { UseMutationOptions, useMutation } from "@tanstack/react-query";

import { getRecordingQueryKey } from "./get-recording";
import { updateRecordingInInfiniteQuery } from "./mark-recording-as-permanent";

const markRecordingAsTemporary = async ({
  orgId,
  id,
}: {
  orgId: string;
  id: string;
}): Promise<string> => {
  const response = await axiosClient.post(
    `/${orgId}/recordings/${id}/mark-as-temporary`,
  );
  return response.data;
};

function useMarkRecordingAsTemporary(
  mutationOptions?: Partial<
    UseMutationOptions<string, HttpError, { id: string; orgId: string }>
  >,
) {
  return useMutation({
    mutationFn: ({ id, orgId }) => markRecordingAsTemporary({ id, orgId }),
    onSuccess: (data, variables, context) => {
      const { id: recordingId, orgId: organisationId } = variables;

      // Update the infinite query cache
      updateRecordingInInfiniteQuery(
        organisationId,
        recordingId,
        (recording) => ({
          ...recording,
          isPermanent: false,
        }),
      );

      // Update the single recording query cache
      queryClient.setQueryData(
        getRecordingQueryKey(organisationId, recordingId),
        (old: PublicRecording) => {
          if (!old) return old;
          return {
            ...old,
            isPermanent: false,
          };
        },
      );

      // Call the original onSuccess if provided
      mutationOptions?.onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
}
export { useMarkRecordingAsTemporary };
